import React, { useMemo } from 'react';
import {
  Brief,
  CandidateHighlightExperienceSummary,
  SmallHeader,
  Gutter,
  CandidateHighlightExperienceDetails,
  CandidateDescription,
  GroupedExperiencesUtil,
  ParagraphHeader,
  TalentExperiences,
  TalentExperiencesHighlightedItem,
  TalentEducation,
  TalentCertifications,
  TalentQualifications,
  TalentLanguages,
} from '@axiom/ui';
import type {
  GroupedExperiences,
  Taxonomy,
  AfcSubmissionCandidate,
  AfcSubmission,
} from '@axiom/validation';
import { SubmissionCandidateModel } from '@axiom/ui-models';

export type MyEngagementsInterviewTalentProfileProps = {
  candidate: AfcSubmissionCandidate;
  groupedExperiences: GroupedExperiences[];
  submission: AfcSubmission | null;
  taxonomy: Taxonomy;
};

export const MyEngagementsInterviewTalentProfile = ({
  candidate,
  groupedExperiences,
  submission,
  taxonomy,
}: MyEngagementsInterviewTalentProfileProps) => {
  const candidateDescription = new SubmissionCandidateModel({
    candidate,
  }).calculatedDescription();

  const { highlightedExperiences, regularGroups } = useMemo(
    () => GroupedExperiencesUtil.splitHighlighted(groupedExperiences),
    [groupedExperiences]
  );

  const numberOfHighlightDetails = [
    (candidate.industries?.length ?? 0) > 0,
    (candidate.lawFirms?.length ?? 0) > 0,
    candidate.addressCity || candidate.addressState || candidate.addressCountry,
  ].filter(Boolean).length;

  return (
    <Brief
      briefMaxChildren={1}
      pattern="secondary"
      name="INTERVIEW_CANDIDATE_BRIEF"
    >
      <CandidateHighlightExperienceSummary
        condensed
        candidate={candidate}
        submission={submission}
        taxonomy={taxonomy}
      />
      {!!candidateDescription && (
        <Gutter vertical="16px" name="INTERVIEW_CANDIDATE_DESCRIPTION">
          <CandidateDescription
            description={candidateDescription}
            calculatedFirstName={candidate.calculatedFirstName}
          />
        </Gutter>
      )}
      {numberOfHighlightDetails > 0 && (
        <>
          <SmallHeader>Highlights</SmallHeader>
          <Gutter bottom="16px" />
          <CandidateHighlightExperienceDetails
            candidate={candidate}
            taxonomy={taxonomy}
          />
        </>
      )}
      {groupedExperiences.length > 0 && (
        <>
          <Gutter vertical="16px">
            <SmallHeader name="INTERVIEW_EXPERIENCE_HEADER">
              Experience
            </SmallHeader>
          </Gutter>
          {highlightedExperiences.length > 0 && (
            <>
              <Gutter bottom="16px">
                <ParagraphHeader name="HIGHLIGHTED_EXPERIENCES_HEADER">
                  Highlighted Experiences
                </ParagraphHeader>
              </Gutter>
              {highlightedExperiences.map((exp, i) => (
                <Gutter key={exp.id} bottom="16px">
                  <TalentExperiencesHighlightedItem
                    name={`HIGHLIGHTED_EXPERIENCE_ITEM_${i}`}
                    experience={exp}
                  />
                </Gutter>
              ))}
            </>
          )}
          <TalentExperiences
            name="TALENT_EXPERIENCES"
            groupedExperiences={regularGroups}
          />
        </>
      )}
      {(candidate.degrees?.length ?? 0) > 0 && (
        <>
          <Gutter vertical="16px">
            <SmallHeader name="INTERVIEW_EDUCATION_HEADER">
              Education
            </SmallHeader>
          </Gutter>
          <TalentEducation candidate={candidate} />
        </>
      )}
      {(candidate.certifications?.length ?? 0) > 0 && (
        <>
          <Gutter vertical="16px">
            <SmallHeader name="INTERVIEW_CERTIFICATIONS_HEADER">
              Accomplishments
            </SmallHeader>
          </Gutter>
          <TalentCertifications candidate={candidate} />
          <Gutter bottom="16px" />
          <TalentQualifications candidate={candidate} />
        </>
      )}
      {(candidate.languages?.length ?? 0) > 0 && (
        <>
          <Gutter vertical="16px">
            <SmallHeader name="INTERVIEW_LANGUAGES_HEADER">
              Languages
            </SmallHeader>
          </Gutter>
          <TalentLanguages candidate={candidate} />
        </>
      )}
    </Brief>
  );
};
